import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  HomeIcon,
  ChevronRightIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  CurrencyEuroIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const QuotazioniPage = () => {
  const navigate = useNavigate();
  const [quotazioni, setQuotazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Stati per i filtri
  const [filtri, setFiltri] = useState({
    stato: '',
    dataInizio: '',
    dataFine: '',
    importoMin: '',
    importoMax: '',
    clienteId: ''
  });

  // Caricamento quotazioni
  useEffect(() => {
    caricaQuotazioni();
  }, [currentPage, searchTerm, filtri]);

  const caricaQuotazioni = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Costruisci i parametri della query
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        ...filtri
      });

      const response = await fetch(`/api/quotations?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setQuotazioni(data.data.quotations || []);
        setTotalPages(data.data.pagination.totalPages || 1);
        setTotalItems(data.data.pagination.totalItems || 0);
      } else {
        console.error('Errore nel caricamento delle quotazioni');
        // Dati mock per sviluppo
        setQuotazioni(getMockQuotazioni());
        setTotalItems(getMockQuotazioni().length);
        setTotalPages(Math.ceil(getMockQuotazioni().length / 20));
      }
    } catch (error) {
      console.error('Errore:', error);
      // Dati mock per sviluppo
      setQuotazioni(getMockQuotazioni());
      setTotalItems(getMockQuotazioni().length);
      setTotalPages(Math.ceil(getMockQuotazioni().length / 20));
    } finally {
      setLoading(false);
    }
  };

  // Dati mock per sviluppo
  const getMockQuotazioni = () => [
    {
      id: 1,
      idPratica: 10,
      codice: 'QUOT-2025-001',
      cliente: { id: 1, nome: 'Mario', cognome: 'Rossi', ragione_sociale: null },
      importo: 25000,
      canone_mensile: 450,
      durata: 36,
      anticipo: 2000,
      chilometraggio: 20000,
      stato: 'inviato',
      data_scadenza: '2025-02-15',
      createdAt: '2025-01-10',
      vettura: { marca: 'BMW', modello: 'Serie 3' }
    },
    {
      id: 2,
      idPratica: 11,
      codice: 'QUOT-2025-002',
      cliente: { id: 2, nome: null, cognome: null, ragione_sociale: 'Azienda Tech SRL' },
      importo: 45000,
      canone_mensile: 780,
      durata: 48,
      anticipo: 5000,
      chilometraggio: 30000,
      stato: 'accettato',
      data_scadenza: '2025-01-25',
      createdAt: '2025-01-08',
      vettura: { marca: 'Mercedes', modello: 'Classe E' }
    },
    {
      id: 3,
      idPratica: 12,
      codice: 'QUOT-2025-003',
      cliente: { id: 3, nome: 'Giulia', cognome: 'Bianchi', ragione_sociale: null },
      importo: 18000,
      canone_mensile: 320,
      durata: 24,
      anticipo: 1500,
      chilometraggio: 15000,
      stato: 'bozza',
      data_scadenza: '2025-02-20',
      createdAt: '2025-01-12',
      vettura: { marca: 'Volkswagen', modello: 'Golf' }
    },
    {
      id: 4,
      idPratica: 13,
      codice: 'QUOT-2025-004',
      cliente: { id: 4, nome: 'Andrea', cognome: 'Verdi', ragione_sociale: null },
      importo: 32000,
      canone_mensile: 590,
      durata: 36,
      anticipo: 3000,
      chilometraggio: 25000,
      stato: 'rifiutato',
      data_scadenza: '2025-01-18',
      createdAt: '2025-01-05',
      vettura: { marca: 'Audi', modello: 'A4' }
    },
    {
      id: 5,
      idPratica: 14,
      codice: 'QUOT-2025-005',
      cliente: { id: 5, nome: null, cognome: null, ragione_sociale: 'Import Export SPA' },
      importo: 55000,
      canone_mensile: 950,
      durata: 48,
      anticipo: 8000,
      chilometraggio: 40000,
      stato: 'scaduto',
      data_scadenza: '2025-01-10',
      createdAt: '2024-12-28',
      vettura: { marca: 'Tesla', modello: 'Model S' }
    }
  ];

  // Funzione per scaricare PDF usando l'endpoint delle pratiche
  const handleDownloadPDF = async (quotazione) => {
    try {
      const token = localStorage.getItem('token');
      
      // Determina l'ID della pratica dalla struttura dei dati (API reale vs mock)
      const praticaId = quotazione.pratica?.id || quotazione.idPratica;
      
      if (!praticaId) {
        toast.error('ID pratica non trovato');
        console.error('Struttura quotazione:', quotazione);
        return;
      }
      
      console.log('Download preventivo:', { quotazioneId: quotazione.id, praticaId });
      
      // Usa l'endpoint delle pratiche che già funziona
      const response = await fetch(`/api/pratica/${praticaId}/preventivi/${quotazione.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `preventivo-${quotazione.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF scaricato con successo');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || 'Errore nel download del PDF');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    }
  };

  // Funzioni helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const getStatoColor = (stato) => {
    switch (stato) {
      case 'bozza':
        return 'bg-gray-100/80 text-gray-800 border border-gray-200/50';
      case 'inviato':
        return 'bg-blue-100/80 text-blue-800 border border-blue-200/50';
      case 'accettato':
        return 'bg-green-100/80 text-green-800 border border-green-200/50';
      case 'rifiutato':
        return 'bg-red-100/80 text-red-800 border border-red-200/50';
      case 'scaduto':
        return 'bg-orange-100/80 text-orange-800 border border-orange-200/50';
      default:
        return 'bg-gray-100/80 text-gray-800 border border-gray-200/50';
    }
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'bozza':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'inviato':
        return <ClockIcon className="h-4 w-4" />;
      case 'accettato':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'rifiutato':
        return <XCircleIcon className="h-4 w-4" />;
      case 'scaduto':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <DocumentTextIcon className="h-4 w-4" />;
    }
  };

  const getClienteNome = (cliente) => {
    // Gestisci il caso in cui cliente è null o undefined
    if (!cliente) {
      return 'Cliente non specificato';
    }
    
    if (cliente.ragione_sociale) {
      return cliente.ragione_sociale;
    }
    
    // Gestisci il caso in cui nome o cognome potrebbero essere null
    const nome = cliente.nome || '';
    const cognome = cliente.cognome || '';
    
    if (!nome && !cognome) {
      return 'Cliente non specificato';
    }
    
    return `${nome} ${cognome}`.trim();
  };

  const handleFiltriReset = () => {
    setFiltri({
      stato: '',
      dataInizio: '',
      dataFine: '',
      importoMin: '',
      importoMax: '',
      clienteId: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/80 via-white/60 to-blue-50/40">
      <div className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Breadcrumb con stile Apple */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="flex space-x-3 items-center">
            <li>
              <Link 
                to="/nlt/" 
                className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/70 backdrop-blur-sm border border-white/20 text-gray-400 hover:text-gray-600 hover:bg-white/90 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <HomeIcon className="h-5 w-5" />
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="h-5 w-5 text-gray-400 mx-2" />
                <div className="px-4 py-2 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20">
                  <span className="text-sm font-semibold text-gray-700">Quotazioni</span>
                </div>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header con glassmorphism */}
        <div className="mb-8">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-xl">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-4 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Lista Quotazioni</h1>
                    <p className="text-gray-600 mt-1">
                      Gestisci tutti i preventivi e le quotazioni inviate ai clienti
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 md:mt-0">
                <Link
                  to="/nlt/preventivatore"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-blue-800 hover:shadow-lg transition-all duration-200 shadow-md"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Nuova Quotazione
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiche rapide con design Apple */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-md">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">Totale Quotazioni</p>
                <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl flex items-center justify-center shadow-md">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">In Attesa</p>
                <p className="text-2xl font-bold text-gray-900">
                  {quotazioni.filter(q => q.stato === 'inviato').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-md">
                <CheckCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">Accettate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {quotazioni.filter(q => q.stato === 'accettato').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-md">
                <CurrencyEuroIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">Valore Totale</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(quotazioni.reduce((sum, q) => sum + q.importo, 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtri e Ricerca con glassmorphism */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl mb-8 border border-white/20 shadow-xl">
          <div className="p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-6 md:space-y-0 md:space-x-6">
              {/* Ricerca */}
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="w-full pl-12 pr-4 py-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    placeholder="Cerca per codice, cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Pulsanti azione */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center px-6 py-3 border border-gray-200/50 font-semibold rounded-2xl transition-all duration-200 ${
                    showFilters 
                      ? 'bg-blue-100/70 text-blue-700 border-blue-200/50 shadow-md' 
                      : 'bg-white/70 text-gray-700 hover:bg-white/90 hover:shadow-md'
                  }`}
                >
                  <FunnelIcon className="h-5 w-5 mr-2" />
                  Filtri
                </button>
              </div>
            </div>

            {/* Filtri avanzati */}
            {showFilters && (
              <div className="mt-8 pt-8 border-t border-gray-200/50">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Stato
                    </label>
                    <select
                      className="w-full px-4 py-3 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                      value={filtri.stato}
                      onChange={(e) => setFiltri({...filtri, stato: e.target.value})}
                    >
                      <option value="">Tutti gli stati</option>
                      <option value="bozza">Bozza</option>
                      <option value="inviato">Inviato</option>
                      <option value="accettato">Accettato</option>
                      <option value="rifiutato">Rifiutato</option>
                      <option value="scaduto">Scaduto</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Data Inizio
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                      value={filtri.dataInizio}
                      onChange={(e) => setFiltri({...filtri, dataInizio: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Data Fine
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                      value={filtri.dataFine}
                      onChange={(e) => setFiltri({...filtri, dataFine: e.target.value})}
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={handleFiltriReset}
                      className="w-full px-6 py-3 bg-white/70 border border-gray-200/50 font-semibold rounded-2xl text-gray-700 hover:bg-white/90 hover:shadow-md transition-all duration-200"
                    >
                      Reset Filtri
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabella quotazioni con design Apple */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/20 shadow-xl overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50">
            <h3 className="text-xl font-bold text-gray-900">
              Quotazioni ({totalItems})
            </h3>
          </div>

          {loading ? (
            <div className="p-16 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
              <p className="text-lg font-semibold text-gray-700">Caricamento quotazioni...</p>
            </div>
          ) : quotazioni.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-400 to-gray-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <DocumentTextIcon className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Nessuna quotazione trovata</h3>
              <p className="text-gray-600 mb-8">Inizia creando la tua prima quotazione</p>
              <Link
                to="/nlt/preventivatore"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-blue-800 hover:shadow-lg transition-all duration-200 shadow-md"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Crea Quotazione
              </Link>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200/50">
                      <th className="px-8 py-4 text-left text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        Codice / Cliente
                      </th>
                      <th className="px-8 py-4 text-center text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        N. Preventivo
                      </th>
                      <th className="px-8 py-4 text-left text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        Veicolo
                      </th>
                      <th className="px-8 py-4 text-left text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        Importo / Canone
                      </th>
                      <th className="px-8 py-4 text-left text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        Durata / Km
                      </th>
                      <th className="px-8 py-4 text-left text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        Stato
                      </th>
                      <th className="px-8 py-4 text-left text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        Scadenza
                      </th>
                      <th className="px-8 py-4 text-center text-sm font-bold text-gray-900 bg-gradient-to-r from-gray-50/50 to-white/30">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200/30">
                    {quotazioni.map((quotazione, index) => (
                      <tr key={quotazione.id} className={`hover:bg-white/40 transition-all duration-200 ${index % 2 === 0 ? 'bg-white/20' : 'bg-white/10'}`}>
                        <td className="px-8 py-6">
                          <div className="flex items-center space-x-4">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${
                              quotazione.cliente?.ragione_sociale 
                                ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                                : 'bg-gradient-to-br from-green-500 to-green-600'
                            }`}>
                              {quotazione.cliente?.ragione_sociale ? (
                                <BuildingOfficeIcon className="h-6 w-6 text-white" />
                              ) : (
                                <UserIcon className="h-6 w-6 text-white" />
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-gray-900">
                                {quotazione.codice || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-600 font-semibold">
                                {getClienteNome(quotazione.cliente)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-center">
                            <div className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-xl border border-indigo-200/50">
                              <DocumentTextIcon className="h-4 w-4 text-indigo-600 mr-2" />
                              <span className="text-sm font-bold text-indigo-700">
                                PREV-{quotazione.id}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-semibold text-gray-900">
                            {quotazione.vettura ? 
                              `${quotazione.vettura.marca} ${quotazione.vettura.modello}` : 
                              'Non specificato'
                            }
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-bold text-gray-900">
                            {formatCurrency(quotazione.importo)}
                          </div>
                          <div className="text-sm text-gray-600 font-semibold">
                            {formatCurrency(quotazione.canone_mensile)}/mese
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-semibold text-gray-900">
                            {quotazione.durata} mesi
                          </div>
                          <div className="text-sm text-gray-600 font-semibold">
                            {quotazione.chilometraggio?.toLocaleString()} km/anno
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span className={`inline-flex items-center px-3 py-2 rounded-xl text-xs font-bold ${getStatoColor(quotazione.stato)}`}>
                            {getStatoIcon(quotazione.stato)}
                            <span className="ml-2 capitalize">{quotazione.stato}</span>
                          </span>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatDate(quotazione.data_scadenza)}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleDownloadPDF(quotazione)}
                              className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl hover:from-blue-600 hover:to-blue-700 hover:shadow-lg transition-all duration-200 shadow-md"
                              title="Scarica PDF"
                            >
                              <ArrowDownTrayIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginazione con stile Apple */}
              {totalPages > 1 && (
                <div className="px-8 py-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">
                        Mostrando{' '}
                        <span className="font-bold text-gray-900">{(currentPage - 1) * 20 + 1}</span>
                        {' '}a{' '}
                        <span className="font-bold text-gray-900">
                          {Math.min(currentPage * 20, totalItems)}
                        </span>
                        {' '}di{' '}
                        <span className="font-bold text-gray-900">{totalItems}</span>
                        {' '}risultati
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-white/70 border border-gray-200/50 font-semibold rounded-xl text-gray-700 hover:bg-white/90 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Precedente
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-10 h-10 rounded-xl font-bold transition-all duration-200 ${
                                currentPage === pageNum
                                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                                  : 'bg-white/70 border border-gray-200/50 text-gray-700 hover:bg-white/90 hover:shadow-md'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 bg-white/70 border border-gray-200/50 font-semibold rounded-xl text-gray-700 hover:bg-white/90 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Successiva
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotazioniPage; 